import { Injectable } from '@angular/core';
import { LoggerService } from '../logging/logger.service';

/**
 * Service to cache Material Symbols Outlined font in localStorage
 * to improve performance and reduce load times on page refresh.
 */
@Injectable({
  providedIn: 'root',
})
export class IconCacheService {
  private readonly STORAGE_KEY = 'material-symbols-font-cache';
  private readonly VERSION_KEY = 'material-symbols-font-version';
  // Update this version when you want to force a cache refresh
  private readonly CURRENT_VERSION = '1.0.0';

  // Google Fonts CSS URL for Material Symbols Outlined
  private readonly FONT_CSS_URL =
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap';

  constructor(private logger: LoggerService) {}

  /**
   * Initialize icon caching system.
   * Should be called early in app initialization (e.g., APP_INITIALIZER).
   */
  async initialize(): Promise<void> {
    try {
      const cachedVersion = localStorage.getItem(this.VERSION_KEY);
      const cachedFont = localStorage.getItem(this.STORAGE_KEY);

      // If cache exists and version matches, use cached font
      if (cachedVersion === this.CURRENT_VERSION && cachedFont) {
        this.injectFontCSS(cachedFont);
        return;
      }

      // Otherwise, fetch and cache the font
      this.logger.info('Fetching Material Symbols font from Google Fonts');
      await this.fetchAndCacheFont();
    } catch (error) {
      this.logger.error('Error initializing icon cache, falling back to CDN:', error);
      // Fallback: Let the browser load from CDN normally
    }
  }

  /**
   * Fetch font CSS from Google Fonts and cache it in localStorage.
   */
  private async fetchAndCacheFont(): Promise<void> {
    try {
      // Fetch the CSS file
      const response = await fetch(this.FONT_CSS_URL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let cssText = await response.text();

      // Extract all font URLs from the CSS and convert to data URIs
      cssText = await this.convertFontUrlsToDataURIs(cssText);

      // Store in localStorage (best-effort; quota may be exceeded on some browsers)
      try {
        localStorage.setItem(this.STORAGE_KEY, cssText);
        localStorage.setItem(this.VERSION_KEY, this.CURRENT_VERSION);
      } catch (cacheError) {
        this.logger.warn(
          'Icon cache quota exceeded; icons will load from CDN on next visit',
          cacheError,
        );
      }

      // Always inject — even if caching failed, the CSS is already in memory
      this.injectFontCSS(cssText);

      this.logger.info('Material Symbols font cached successfully');
    } catch (error) {
      this.logger.error('Error fetching and caching font:', error);
      throw error;
    }
  }

  /**
   * Convert all font URLs in CSS to data URIs for offline caching.
   */
  private async convertFontUrlsToDataURIs(cssText: string): Promise<string> {
    // Match all url() references in the CSS
    const urlRegex = /url\(([^)]+)\)/g;
    const matches = [...cssText.matchAll(urlRegex)];

    for (const match of matches) {
      const url = match[1].replace(/['"]/g, ''); // Remove quotes

      try {
        // Fetch the font file
        const response = await fetch(url);
        if (!response.ok) continue;

        const blob = await response.blob();
        const dataUri = await this.blobToDataUri(blob);

        // Replace the URL in the CSS text
        cssText = cssText.replace(match[0], `url(${dataUri})`);
      } catch (error) {
        this.logger.warn(`Failed to convert font URL to data URI: ${url}`, error);
      }
    }

    return cssText;
  }

  /**
   * Convert a Blob to a data URI.
   */
  private blobToDataUri(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Inject font CSS into the document head.
   */
  private injectFontCSS(cssText: string): void {
    // Check if style element already exists
    let styleElement = document.getElementById('material-symbols-cache') as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'material-symbols-cache';
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = cssText;
  }

  /**
   * Clear cached fonts (useful for debugging or forcing refresh).
   */
  clearCache(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.VERSION_KEY);
    const styleElement = document.getElementById('material-symbols-cache');
    if (styleElement) {
      styleElement.remove();
    }
    this.logger.info('Icon cache cleared');
  }

  /**
   * Get cache statistics.
   */
  getCacheStats(): { isCached: boolean; version: string | null; size: number } {
    const cachedFont = localStorage.getItem(this.STORAGE_KEY);
    const cachedVersion = localStorage.getItem(this.VERSION_KEY);

    return {
      isCached: !!cachedFont,
      version: cachedVersion,
      size: cachedFont ? cachedFont.length : 0,
    };
  }
}
